import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Box, TextField, Typography, Grid, Autocomplete, InputAdornment, IconButton } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { AgeGroup, CustomerGender, FileKind, PersonDetails, PersonDetailsFormHandle } from 'Application/models';
import { KycSingleFileUploader } from '../file-uploading';
import { getAgeFromDOB, InputValidators } from 'Application/utils';
import { Search } from '@mui/icons-material';
import { useCreateUserStore } from 'Application/store';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LoadingCircleLoop } from '../animations';
import { AxiosError } from 'axios';
import MuiPhoneNumber from 'mui-phone-number';
import { useQuery } from '@tanstack/react-query';
import { TextFieldSkeleton } from '../TextFieldSkeleton';
import { ConfigsService } from 'Application/service';

interface PersonDetailsFormProps {
    ageGroup: AgeGroup;
    isMainCustomer: boolean;
    isAStudent?: boolean;
    isFamilyPlan?: boolean;
    isMultipleIndividualPlan?: boolean;
    nationalities: string[];
    onChange: (updatedDetails: Partial<PersonDetails>) => void;
    personDetails: PersonDetails;
    initialValues: Omit<PersonDetails, 'kraFile' | 'passportFile' | 'medicalFile' | 'studentFile'>;
    formKey: string;
    activeFormKey: string;
    setActiveFormKey: () => void;
}

export const PersonDetailsForm = forwardRef<PersonDetailsFormHandle, PersonDetailsFormProps>(
    (
        {
            nationalities,
            ageGroup,
            isMainCustomer,
            onChange: onPersonDetailsChange,
            personDetails,
            initialValues,
            isAStudent = false,
            isFamilyPlan = false,
            isMultipleIndividualPlan = false,
            formKey,
            activeFormKey,
            setActiveFormKey
        },
        ref
    ) => {
        const { getCustomerDetails, customerDetails, isLoading } = useCreateUserStore((state) => ({
            getCustomerDetails: state.getCustomerDetails,
            customerDetails: state.customerDetails,
            isLoading: state.isLoading
        }));

        const PersonDetailsSchema = Yup.object().shape({
            firstName: InputValidators.name,
            otherNames: InputValidators.name,
            phoneNumber: isMainCustomer ? InputValidators.phone : InputValidators.optionalField,
            emailAddress: isMainCustomer ? InputValidators.email : InputValidators.optionalField,
            dateOfBirth: InputValidators.date,
            taxNumber: Yup.string().when(['nationality'], {
                is: (nationality: string) => isMainCustomer && nationality === 'Kenyan',
                then: () => InputValidators.kraPinRequired,
                otherwise: () => InputValidators.optionalField
            }),
            identificationNumber: InputValidators.passport,
            nationality: isMainCustomer ? InputValidators.requiredString : InputValidators.optionalField,
            gender: isMainCustomer ? InputValidators.requiredString : InputValidators.optionalField,
            occupation: isMainCustomer
                ? Yup.object({
                      code: isAStudent ? InputValidators.optionalField : InputValidators.requiredString,
                      description: isAStudent ? InputValidators.optionalField : InputValidators.requiredString
                  })
                      .nullable()
                      .required('Occupation is required')
                : Yup.object({
                      code: InputValidators.optionalField,
                      description: InputValidators.optionalField
                  }).nullable()
        });
        const {
            control,
            trigger,
            formState: { errors },
            setValue,
            watch,
            reset
        } = useForm({
            defaultValues: initialValues,
            resolver: yupResolver(PersonDetailsSchema),
            mode: 'onTouched'
        });

        const values = watch();

        const getExistingDetails = async (passportNumber: string) => {
            if (errors.identificationNumber !== undefined) {
                return;
            }

            setActiveFormKey();

            try {
                const response = await getCustomerDetails(passportNumber);

                if (response) {
                    setValue('firstName', response.firstName);
                    setValue('otherNames', response.otherNames);
                    setValue('phoneNumber', response.phoneNumber);
                    setValue('emailAddress', response.emailAddress);
                    setValue('dateOfBirth', response.dateOfBirth);
                    setValue('identificationNumber', response.identificationNumber);
                    setValue('occupation', response.occupation);
                    setValue('gender', response.gender.toTitle());
                    setValue('nationality', response.nationality);

                    onPersonDetailsChange({
                        firstName: response.firstName,
                        otherNames: response.otherNames,
                        phoneNumber: response.phoneNumber,
                        emailAddress: response.emailAddress,
                        dateOfBirth: response.dateOfBirth,
                        identificationNumber: response.identificationNumber,
                        occupation: response.occupation,
                        gender: response.gender.toUpperCase(), //* ALready returned as uppercase from API
                        nationality: response.nationality
                    });

                    toast.info('Existing customer details found');
                }
            } catch (error) {
                //TODO: Handle other errors well
                if (error instanceof AxiosError) {
                    if (error.response?.status === 404) {
                        toast.info('Enter details for the new customer');
                        reset();
                        setValue('identificationNumber', passportNumber);
                        return;
                    }
                    toast.info("We're sorry, the search couldn't be completed right now. Please try again later.");
                } else {
                    toast.info("We're sorry, the search couldn't be completed right now. Please try again later.");
                }
            }
        };

        const { data: allOccupationsData, isLoading: isGetAllOccupationsLoading } = useQuery({
            queryKey: ['getAllOccupations'],
            queryFn: ConfigsService.getAllOccuptaions,
            retry: false
        });

        const minDate =
            ageGroup === AgeGroup.KENYA_UNDER_18
                ? dayjs().subtract(17, 'year')
                : ageGroup === AgeGroup.KENYA_18_TO_69
                  ? dayjs().subtract(69, 'year')
                  : ageGroup === AgeGroup.KENYA_70_TO_75
                    ? dayjs().subtract(75, 'year')
                    : ageGroup === AgeGroup.KENYA_76_TO_80
                      ? dayjs().subtract(80, 'year')
                      : ageGroup === AgeGroup.KENYA_81_TO_85
                        ? dayjs().subtract(85, 'year')
                        : dayjs().subtract(85, 'year');
        const maxDate =
            ageGroup === AgeGroup.KENYA_UNDER_18
                ? dayjs().subtract(1, 'year')
                : ageGroup === AgeGroup.KENYA_18_TO_69
                  ? dayjs().subtract(18, 'year')
                  : ageGroup === AgeGroup.KENYA_70_TO_75
                    ? dayjs().subtract(70, 'year')
                    : ageGroup === AgeGroup.KENYA_76_TO_80
                      ? dayjs().subtract(76, 'year')
                      : ageGroup === AgeGroup.KENYA_81_TO_85
                        ? dayjs().subtract(81, 'year')
                        : dayjs().subtract(18, 'year');

        useImperativeHandle(ref, () => ({
            validateAndSubmit: async () => {
                const result = await trigger(); //* Trigger validation
                if (!result) {
                    return false;
                }
                return true;
            }
        }));

        useEffect(() => {
            //* Update the date of birth in case onChange is not triggered for dateOfBirth
            onPersonDetailsChange({ dateOfBirth: values.dateOfBirth });
        }, []);

        return (
            <Box className="border border-slate-200 rounded-md pt-10 pb-5 px-6 shadow-sm hover:shadow-md">
                {isLoading && activeFormKey === formKey && customerDetails === null ? (
                    <Box className="flex flex-col justify-center items-center min-h-[100px]">
                        <LoadingCircleLoop />
                        <Typography className="font-medium text-md">Searching ...</Typography>
                    </Box>
                ) : (
                    <form>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="identificationNumber"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            fullWidth
                                            label="Passport Number"
                                            {...field}
                                            error={!!errors.identificationNumber}
                                            helperText={errors.identificationNumber?.message}
                                            margin="normal"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={async () => await getExistingDetails(field.value)} edge="end">
                                                            <Search fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                            // onBlur={async () => await getExistingDetails(field.value)}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                onPersonDetailsChange({ identificationNumber: e.target.value });
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            {isMainCustomer && (
                                <Grid item xs={12} sm={6}>
                                    <Controller
                                        name="nationality"
                                        control={control}
                                        render={({ field }) => (
                                            <Autocomplete
                                                {...field}
                                                options={nationalities.map((nationality) => nationality)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        margin="normal"
                                                        {...params}
                                                        label="Nationality"
                                                        fullWidth
                                                        error={!!errors.nationality}
                                                        helperText={errors.nationality?.message}
                                                    />
                                                )}
                                                onChange={(_event, value) => {
                                                    setValue('nationality', value ?? 'Kenyan');
                                                    onPersonDetailsChange({ nationality: value ?? 'Kenyan' });
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="firstName"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            fullWidth
                                            label="First Name"
                                            {...field}
                                            error={!!errors.firstName}
                                            helperText={errors.firstName?.message}
                                            margin="normal"
                                            onChange={(e) => {
                                                field.onChange(e);
                                                onPersonDetailsChange({ firstName: e.target.value });
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="otherNames"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            fullWidth
                                            label="Other Names"
                                            {...field}
                                            error={!!errors.otherNames}
                                            helperText={errors.otherNames?.message}
                                            margin="normal"
                                            onChange={(e) => {
                                                field.onChange(e);
                                                onPersonDetailsChange({ otherNames: e.target.value });
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="dateOfBirth"
                                    control={control}
                                    render={({ field: { value, ...field } }) => (
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                className="w-full"
                                                {...field}
                                                label="Date of birth"
                                                value={value ? dayjs(value) : null}
                                                minDate={minDate}
                                                maxDate={maxDate}
                                                format={'YYYY-MM-DD'}
                                                slotProps={{
                                                    textField: {
                                                        margin: 'normal',
                                                        error: !!errors.dateOfBirth,
                                                        helperText: errors.dateOfBirth?.message,
                                                        inputProps: {
                                                            readOnly: true
                                                        }
                                                    }
                                                }}
                                                onChange={(newValue) => {
                                                    field.onChange(newValue ? newValue.format('YYYY-MM-DD') : null);
                                                    onPersonDetailsChange({
                                                        dateOfBirth: newValue ? newValue.format('YYYY-MM-DD') : undefined
                                                    });
                                                }}
                                            />
                                        </LocalizationProvider>
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    margin="normal"
                                    label="Age"
                                    value={dayjs().diff(watch('dateOfBirth'), 'year')}
                                    fullWidth
                                    disabled
                                />
                            </Grid>

                            {isMainCustomer && (
                                <Grid item xs={12} sm={6}>
                                    <Controller
                                        name="gender"
                                        control={control}
                                        render={({ field }) => (
                                            <Autocomplete
                                                {...field}
                                                options={Object.values(CustomerGender).map((genderValue) => genderValue.toTitle())}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Gender"
                                                        fullWidth
                                                        margin="normal"
                                                        error={!!errors.gender}
                                                        helperText={errors.gender?.message}
                                                    />
                                                )}
                                                onChange={(_event, value) => {
                                                    setValue('gender', value ?? CustomerGender.MALE.toTitle());
                                                    onPersonDetailsChange({ gender: value?.toUpperCase() ?? CustomerGender.MALE });
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}

                            {(isMainCustomer || isMultipleIndividualPlan) && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name="phoneNumber"
                                            control={control}
                                            render={({ field }) => (
                                                <MuiPhoneNumber
                                                    required
                                                    label="Phone Number"
                                                    fullWidth
                                                    defaultCountry="ke"
                                                    variant="outlined"
                                                    margin="normal"
                                                    {...field}
                                                    onChange={(value) => {
                                                        field.onChange(value);
                                                        onPersonDetailsChange({ phoneNumber: value.toString() });
                                                    }}
                                                    error={!!errors.phoneNumber}
                                                    helperText={errors.phoneNumber?.message}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name="emailAddress"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    fullWidth
                                                    label="Email Address"
                                                    {...field}
                                                    error={!!errors.emailAddress}
                                                    helperText={errors.emailAddress?.message}
                                                    margin="normal"
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        onPersonDetailsChange({ emailAddress: e.target.value });
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {values.nationality === 'Kenyan' && (
                                        <Grid item xs={12} sm={6}>
                                            <Controller
                                                name="taxNumber"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        fullWidth
                                                        label="KRA Pin Number"
                                                        {...field}
                                                        error={!!errors.taxNumber}
                                                        helperText={errors.taxNumber?.message}
                                                        margin="normal"
                                                        onChange={(e) => {
                                                            field.onChange(e);
                                                            onPersonDetailsChange({ taxNumber: e.target.value });
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    )}

                                    {!isAStudent && (
                                        <Grid item xs={12} sm={6}>
                                            {isGetAllOccupationsLoading ? (
                                                <TextFieldSkeleton />
                                            ) : (
                                                <Controller
                                                    name="occupation"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Autocomplete
                                                            {...field}
                                                            onChange={(_event, value) => {
                                                                if (value) {
                                                                    setValue('occupation', value);
                                                                    onPersonDetailsChange({ occupation: value });
                                                                }
                                                            }}
                                                            options={allOccupationsData!}
                                                            getOptionLabel={(option) => option.description!}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    required
                                                                    margin="normal"
                                                                    {...params}
                                                                    label="Occupation"
                                                                    fullWidth
                                                                    error={!!errors.occupation}
                                                                    helperText={errors.occupation?.message}
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </Grid>
                                    )}
                                </>
                            )}

                            <Grid item xs={12}>
                                {values.identificationNumber && values.dateOfBirth ? (
                                    <Box>
                                        <Typography>Please upload new documents for the following</Typography>
                                        <KycSingleFileUploader
                                            fileKind={FileKind.PASSPORT}
                                            onDeleteFile={() => {
                                                onPersonDetailsChange({ passportFile: null });
                                            }}
                                            onFileChange={(files) => {
                                                onPersonDetailsChange({
                                                    passportFile: { file: files[0], fileName: files[0].name, fileKind: FileKind.PASSPORT }
                                                });
                                            }}
                                            selectedFile={personDetails.passportFile?.file}
                                        />

                                        {/* ONLY FOR MAIN CUSTOMER */}
                                        {/* {((isMainCustomer && !isAStudent) || (isMainCustomer && isAStudent && (getAgeFromDOB(watch('dateOfBirth')) >= 18))) && (
                                                <KycSingleFileUploader
                                                    fileKind={FileKind.KRA}
                                                    onDeleteFile={() => {
                                                        onPersonDetailsChange({ kraFile: null })
                                                    }}
                                                    onFileChange={(files) => {
                                                        onPersonDetailsChange({
                                                            kraFile: { file: files[0], fileName: files[0].name, fileKind: FileKind.KRA }
                                                        });
                                                    }}
                                                    selectedFile={personDetails.kraFile?.file}
                                                />
                                            )} */}

                                        {/* MANDATORY FOR A STUDENT THAT IS A MAIN CUSTOMER */}
                                        {/* NOTE: Student plan has no Group type of cover */}
                                        {(isAStudent || (isFamilyPlan && !isMainCustomer && getAgeFromDOB(watch('dateOfBirth')) >= 18)) && (
                                            <KycSingleFileUploader
                                                fileKind={FileKind.FULL_TIME_EDUCATION}
                                                onDeleteFile={() => {
                                                    onPersonDetailsChange({ studentFile: null });
                                                }}
                                                onFileChange={(files) => {
                                                    onPersonDetailsChange({
                                                        studentFile: {
                                                            file: files[0],
                                                            fileName: files[0].name,
                                                            fileKind: FileKind.FULL_TIME_EDUCATION
                                                        }
                                                    });
                                                }}
                                                selectedFile={personDetails.studentFile?.file}
                                            />
                                        )}
                                    </Box>
                                ) : (
                                    <Box className="border border-dotted mb-4 border-slate-400 rounded-md py-4">
                                        <Typography className="text-center text-orange-700">
                                            Ensure passport number is correct before uploading files
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                            {/* <Grid item xs={12}>
                                    <Button
                                        style={{ display: 'none' }}
                                        type="submit"
                                    >
                                        Submit
                                    </Button>
                                </Grid> */}
                        </Grid>
                    </form>
                )}
            </Box>
        );
    }
);
